import { createTheme, adaptV4Theme } from '@mui/material/styles';
import * as dTokens from 'ui-common/components/design-tokens';

// Define region-specific configurations
const REGIONS = {
  US: {
    primary: dTokens.res_brand_color_007,
    secondary: '#B02543', // resmedUsColor
    secondaryHover: '#a51e39',
    commonBlack: '#003967'
  },
  AU: {
    primary: dTokens.res_brand_color_007,
    secondary: dTokens.res_brand_color_001, // resMedLightBlue
    secondaryHover: '#00497A',
    commonBlack: dTokens.res_blue_grey_900
  }
  // Add new regions here following the same structure
};

// Get current region from environment or default to AU
const getCurrentRegion = () => {
  const region = process.env.REACT_APP_BUILD_REGION;
  return REGIONS[region] || REGIONS.AU;
};

// Common colors that don't change by region
const COMMON_COLORS = {
  grey: '#E3E7ED',
  red: '#B02643',
  error: dTokens.color_text_error,
  success: dTokens.color_text_success,
  extraLight: dTokens.res_grey_50,
  light: dTokens.res_grey_100,
  lightText: dTokens.res_grey_600,
  darkerText: dTokens.res_grey_700,
  white: dTokens.color_white
};

// Hex transparency value to append to hex colour values
const TRANSPARENCY = {
  tenPercent: '1A'
};

// Typography settings
const TYPOGRAPHY = {
  fontFamily: ['Open Sans Bold', 'Open Sans', 'sans-serif'].join(',')
};

// Create the theme
const createPatientTheme = () => {
  const currentRegion = getCurrentRegion();
  
  return createTheme(
    adaptV4Theme({
      palette: {
        primary: {
          main: currentRegion.primary,
        },
        secondary: {
          main: currentRegion.secondary,
        },
        tertiary: {
          main: '#6F2C91',
        },
        settings: {
          titlebar: '#000000',
          border: '#ebebeb',
        },
        table: {
          header: COMMON_COLORS.grey,
        },
        text: {
          primary: dTokens.res_grey_800,
        },
        background: {
          paper: dTokens.res_grey_50,
        },
        common: {
          black: currentRegion.commonBlack,
          white: COMMON_COLORS.white,
        },
      },
      typography: {
        fontFamily: TYPOGRAPHY.fontFamily,
      },
      overrides: {
        MuiFormLabel: {
          root: {
            '&$focused': {
              color: '#000000',
            },
          },
        },
        MuiButton: {
          root: {
            fontFamily: TYPOGRAPHY.fontFamily,
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: 'normal',
            letterSpacing: '1px',
            textTransform: 'none',
            padding: '10px 24px',
            height: '34px',
            borderRadius: '24px',
            boxShadow: 'none',
          },
          contained: {
            color: dTokens.c_btn_primary_text_color,
            backgroundColor: currentRegion.secondary,
            alignSelf: 'stretch',
            '&:hover': {
              backgroundColor: currentRegion.secondaryHover,
              boxShadow: 'none',
            },
            '&:active': {
              backgroundColor: currentRegion.secondary,
              boxShadow: 'none',
            },
            '&:disabled': {
              color: dTokens.c_btn_primary_text_color,
              backgroundColor: currentRegion.secondary,
              opacity: dTokens.c_btn_opacity_disabled,
              boxShadow: 'none',
            },
          },
          outlined: {
            color: currentRegion.secondary,
            backgroundColor: dTokens.c_btn_secondary_bgcolor,
            borderWidth: '2px',
            borderStyle: dTokens.c_btn_secondary_border_style,
            borderColor: currentRegion.secondary,
            '&:hover': {
              color: currentRegion.secondaryHover,
              borderColor: `${currentRegion.secondaryHover} !important`,
              backgroundColor: dTokens.c_btn_secondary_bgcolor,
              borderWidth: dTokens.c_btn_secondary_hover_border_width,
            },
            '&:active': {
              color: currentRegion.secondary,
              borderColor: currentRegion.secondary,
              backgroundColor: `${currentRegion.secondary}${TRANSPARENCY.tenPercent}`,
            },
            '&:disabled': {
              color: currentRegion.secondary,
              backgroundColor: dTokens.c_btn_secondary_bgcolor,
              borderWidth: dTokens.c_btn_secondary_border_width,
              borderStyle: dTokens.c_btn_secondary_border_style,
              borderColor: `${currentRegion.secondary} !important`,
              opacity: dTokens.c_btn_opacity_disabled,
            },
          },
          text: {
            padding: '10px 12px',
            color: currentRegion.secondary,
            '&:hover': {
              color: currentRegion.secondaryHover,
              backgroundColor: dTokens.res_grey_50,
            },
            '&:active': {
              color: currentRegion.secondary,
              background: 'none',
            },
            '&:disabled': {
              color: currentRegion.secondary,
              opacity: dTokens.c_btn_opacity_disabled,
            },
          },
        },
        MuiChip: {
          root: {
            borderRadius: 4,
          },
        },
        MuiTableCell: {
          root: {
            borderBottom: 'none',
            fontSize: '16px',
            lineHeight: '15px',
          },
        },
        MuiListItem: {
          root: {
            '&$selected': { backgroundColor: COMMON_COLORS.grey },
          },
          gutters: {
            paddingTop: '18px',
            paddingBottom: '18px',
          },
        },
        MuiTablePagination: {
          toolbar: {
            backgroundColor: COMMON_COLORS.grey,
          },
        },
        MuiDialogContent: {
          root: {
            fontSize: '16px',
            lineHeight: '20px',
          },
        },
        MuiDialogContentText: {
          root: {
            color: '#000000',
          },
        },
        MuiImageListItemBar: {
          title: {
            fontSize: '14px',
            color: '#000000',
          },
        },
      },
    })
  );
};

const patientTheme = createPatientTheme();

// Export common colors for use elsewhere in the application
export default patientTheme;
export const themeColors = {
  ...COMMON_COLORS,
  blue: dTokens.res_brand_color_007,
  lightBlue: dTokens.res_brand_color_001,
  // Add the current region's colors
  ...getCurrentRegion()
};
